<template>
  <div
    :class="{
      'init-step-container': true,
      'init-step-container--disabled': isLoadingGetCalendarInputsResponse || isSavingNewOrderAndSourceValues,
    }"
    ref="domRefRoot"
  >
    <!-- Header main -->
    <div class="header-main init-step-container__header-main">
      <div class="header-main__title-row">
        <CalendarPicker :customerId="customerId" suffix="Config" />
      </div>

      <div class="header-main__subtitle">These preferences and inputs help AI shape your content calendar.</div>

      <HowContentCalendarWorksSpoiler class="header-main__how-does-it-work-spoiler" />

      <!--<NoteWithIcon class="header-main__note" color="green">
        <template #text>
          <div>
            <h4 class="global-h4">Call out Events and Themes to highlight</h4>
            <p class="global-p">Keep it light. Start with broad themes; you can refine weekly details in the next step.</p>
          </div>
        </template>
      </NoteWithIcon>-->
    </div>
    <!-- / Header main -->

    <Loader v-if="isLoadingGetCalendarInputsResponse" />

    <template v-else>
      <!-- Connected Social Accounts -->
      <div class="header-secondary init-step-container__header-secondary" style="margin-bottom: 12px">
        <h2 class="header-secondary__title">Connected Social Accounts</h2>
        <!--<div class="header-secondary__subtitle">Link social accounts and set your default posting plan.</div>-->
      </div>

      <div class="init-step-container__social-accounts-wrap">
        <div class="controls-container__two-col">
          <div class="controls-container__content">
            <div v-if="socialChannels.length" class="channels">
              <div v-for="(ch, idx) in socialChannels" :key="idx" class="channel">
                <IconEmbedded :name="channelToIcon(ch.channelType)" :size="22" style="margin-right: 8px" />
                <span class="channel__label">{{ ch.channelType }}</span>
                <span v-if="isChannelAuthNeeded(ch.status)" class="channel__status-badge">Auth Needed</span>
                <a v-if="ch.displayName && channelProfileUrl(ch)" class="channel__id channel__profile-link" :href="channelProfileUrl(ch)" target="_blank" rel="noopener noreferrer">
                  · {{ ch.displayName }}
                  <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
                </a>
                <span v-else-if="ch.displayName" class="channel__id">· {{ ch.displayName }}</span>
              </div>
            </div>
            <div v-else class="channels--empty"><b>No accounts linked yet.</b></div>

            <div v-if="authNeededChannels.length" class="channel-reauth-wrap">
              <SrpButton color="red" @click="openSocialAccountConnectPrepModal" :isDisabled="isCalendarTestMode || isRedirectingToPortal">
                <template #icon>
                  <LoadingIcon v-if="isRedirectingToPortal" :size="22" />
                  <IconEmbedded v-else name="warning_2-5" :size="20" />
                </template>
                {{ isRedirectingToPortal ? "Redirecting..." : "Re-Auth Needed" }}
              </SrpButton>
            </div>

            <div v-else style="display: flex; align-items: center; gap: 8px; margin-top: 18px">
              <SrpButton
                class="link-button"
                :fill="socialChannels.length ? 'outlined' : 'solid'"
                color="teal"
                :isDisabled="isCalendarTestMode || isRedirectingToPortal"
                @click="openSocialAccountConnectPrepModal"
              >
                <template #icon>
                  <LoadingIcon v-if="isRedirectingToPortal" :size="22" />
                  <IconEmbedded v-else name="plus_3" :size="22" />
                </template>
                {{ isRedirectingToPortal ? "Redirecting..." : "Connect Accounts" }}</SrpButton
              >
              <InfoIconWithTooltip :width="250">New accounts will automatically be added to unapproved posts.</InfoIconWithTooltip>
              <span v-if="isCalendarTestMode" style="margin-left: 5px"><b>Disabled in test mode.</b></span>
            </div>
          </div>

          <div class="controls-container__content">
            <RequestCalendarLink :customerId="customerId" style="margin-bottom: 10px" />
            <NoteWithIcon color="green" size="tiny">
              <template #text>
                <div>
                  <h5 class="global-h4">We use bundle.social</h5>
                  <p class="global-p">Connected accounts will be authorized for bundle.social to allow us to schedule and publish posts on your behalf.</p>
                </div>
              </template>
            </NoteWithIcon>
          </div>
        </div>
      </div>

      <div class="init-step-container__divider"></div>

      <!-- Calendar Name (only shown when multiple calendars exist) -->
      <template v-if="showCalendarName && userSettings">
        <div class="header-secondary init-step-container__header-secondary init-step-container__calendar-name-header">
          <h2 class="header-secondary__title">Calendar Name</h2>
        </div>
        <div class="init-step-container__calendar-name-wrap">
          <input v-model="userSettings.calendarName" class="global-text-input" type="text" placeholder="Calendar name..." style="max-width: 350px" />
        </div>
      </template>

      <!-- Posting Schedule -->
      <div class="header-secondary init-step-container__header-secondary" style="margin-bottom: 5px">
        <h2 class="header-secondary__title">Posting Schedule</h2>
        <div class="header-secondary__subtitle">{{ postsPerWeekMessage }}</div>
      </div>

      <div class="init-step-container__schedule-wrap">
        <div class="controls-container__two-col">
          <div class="controls-container__content">
            <!--<div style="margin-bottom: 12px">
              <div class="schedule__legend" style="font-weight: bold">Weekly posting times</div>
            </div>-->
            <div class="schedule">
              <div class="schedule__slots">
                <div v-for="(slot, index) in slotsForCurrentPlan" :key="index" class="slot-item">
                  <select class="simple-select" v-model.number="slot.day">
                    <option v-for="d in daysOfWeek" :key="d.value" :value="d.value">{{ d.label }}</option>
                  </select>
                  <input class="global-text-input" v-model="slot.time" type="time" step="60" style="max-width: 120px" />
                  <div style="display: flex; align-items: center; gap: 4px">
                    <button
                      v-if="slotsForCurrentPlan.length > 1"
                      type="button"
                      class="slot-item__delete-btn"
                      @click="openRemoveSlotModal(index)"
                      :style="{ opacity: slotsForCurrentPlan.length <= 1 ? 0.4 : 1, cursor: slotsForCurrentPlan.length <= 1 ? 'not-allowed' : 'pointer' }"
                    >
                      <IconEmbedded name="trashcan-alt_2" :size="18" color="#666" />
                    </button>
                    <!--<InfoIconWithTooltip v-if="slotsForCurrentPlan.length <= 1" :width="200">At least one posting day is required.</InfoIconWithTooltip>-->
                  </div>
                </div>
              </div>

              <div v-if="userSettings" style="margin-top: 16px">
                <label class="form-field">
                  <select class="simple-select" v-model="timeZone" style="max-width: 100%" :disabled="isCalendarTestMode">
                    <option v-for="tz in COMMON_US_TIMEZONES" :key="tz.zoneId" :value="tz.zoneId">{{ tz.name }}</option>
                  </select>
                </label>
                <span v-if="isCalendarTestMode" style="margin-top: 5px; display: block"><b>Disabled in test mode.</b></span>
              </div>

              <LinkWithIcon v-if="slotsForCurrentPlan.length < 3" isDottedUnderline color="gray" @click="isAddDayModalVisible = true" style="margin-top: 10px">
                <template #icon><IconEmbedded name="plus_2-5" :size="18" /></template>
                Add Posting Day
              </LinkWithIcon>
              <!--<div v-else style="margin-top: 10px; display: flex; align-items: center; gap: 4px; opacity: 0.5">
                <InfoIconWithTooltip :width="200">Maximum of 3 auto-generated posting days is currently allowed.</InfoIconWithTooltip>
              </div>-->
            </div>
          </div>

          <div class="controls-container__content">
            <div v-if="userSettings" class="form-field" style="margin-bottom: 20px">
              <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap">
                <span style="font-weight: bold">Generate posts for</span>
                <select class="simple-select" v-model.number="userSettings.weeksToGenPosts">
                  <option :value="1">1</option>
                  <option :value="2">2</option>
                  <option :value="3">3</option>
                  <option :value="4">4</option>
                </select>
                <span style="font-weight: bold">weeks at a time.</span>
                <InfoIconWithTooltip :width="250">Changes take effect the next time posts are generated.</InfoIconWithTooltip>
              </div>
              <LinkWithIcon isDottedUnderline @click="isMoreSettingsModalVisible = true" style="margin-top: 18px">
                <template #icon>
                  <IconEmbedded name="gear_2" :size="18" />
                </template>
                More settings
              </LinkWithIcon>
            </div>

            <!--<div v-if="userSettings" class="form-field" style="margin-bottom: 20px">
              <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap">
                <span style="font-weight: bold">Use </span>
                <select class="simple-select" v-model.number="userSettings.mediaPerPost">
                  <option :value="1">1</option>
                  <option :value="2">2</option>
                  <option :value="3">3</option>
                  <option :value="4">4</option>
                  <option :value="5">5</option>
                  <option :value="6">6</option>
                  <option :value="7">7</option>
                </select>
                <span style="font-weight: bold">images per post.</span>
              </div>
            </div>-->
          </div>
        </div>

        <div style="display: flex; align-items: center; gap: 10px; margin-top: 20px">
          <SrpButton @click="saveSocialSettings" :isDisabled="isSavingSocialSettings">
            <template #icon>
              <LoadingIcon v-if="isSavingSocialSettings" :size="18" />
              <IconEmbedded v-else name="check_3" :size="18" />
            </template>
            Save
          </SrpButton>
          <div v-if="isSavingSocialSettings" class="save-msg">Saving...</div>
          <div v-else-if="justSavedSocial" class="save-msg">Saved!</div>
        </div>
      </div>

      <div class="init-step-container__divider"></div>

      <SavedLocationsSection :customerId="props.customerId" :isDebugMode="isDebugUI" />

      <div class="init-step-container__divider"></div>

      <!-- Generating Interests -->
      <div v-if="isLoadingInterests" style="margin-bottom: 50px; display: flex; flex-direction: column; justify-content: center; align-items: center">
        <LoaderNew style="width: 70px; height: 70px; margin-bottom: 10px" :borderWidth="3" />
        <div class="global-p" style="text-align: center">
          <h2>Generating Interests</h2>
          We're generating interests based on your content!<br />
          This usually takes about a minute.<EllipsisAnimated />
        </div>
      </div>

      <!-- Interests -->
      <template v-if="!isLoadingInterests">
        <div class="header-secondary init-step-container__header-secondary init-step-container__header-secondary--with-toggle">
          <div>
            <h2 class="header-secondary__title">Your Interests</h2>
            <div class="header-secondary__subtitle">Specify interest areas you would like to see in your content calendar.</div>
          </div>
          <!--<SrpSwitch v-model:isChecked="showInterestsSection">{{ showInterestsSection ? "Enabled" : "Disabled" }}</SrpSwitch>-->
        </div>

        <div v-if="showInterestsSection && userSettings" class="init-step-container__interests-wrap">
          <textarea
            v-model="userSettings.excludeFromIndex.interests"
            class="init-step-container__interests-textarea"
            placeholder="e.g., local festivals, outdoor activities, family events..."
            rows="3"
          ></textarea>
        </div>

        <!-- Weekly/Monthly Cadence -->
        <div class="header-secondary init-step-container__header-secondary" style="margin-bottom: 5px">
          <h2 class="header-secondary__title">Weekly or Monthly Posting Rhythm</h2>
          <div class="header-secondary__subtitle">Optional guidance for post themes you want repeated on a weekly or monthly rhythm.</div>
        </div>

        <div v-if="userSettings" class="init-step-container__interests-wrap">
          <textarea
            v-model="userSettings.excludeFromIndex.weeklyCadence"
            class="init-step-container__interests-textarea"
            placeholder="e.g. Each week reserve one post for a local business feature and one for lodging.
or: Prioritize arts early in the month, restaurants later in the month, and weave in upcoming events throughout."
            rows="4"
          ></textarea>
        </div>

        <!-- Brand Voice -->
        <div class="header-secondary init-step-container__header-secondary" style="margin-bottom: 5px">
          <h2 class="header-secondary__title">Brand Voice</h2>
          <div class="header-secondary__subtitle">Describe how your organization should sound in social posts (tone, style, words to use or avoid).</div>
        </div>

        <div v-if="userSettings" class="init-step-container__interests-wrap">
          <textarea
            v-model="userSettings.excludeFromIndex.brandVoice"
            class="init-step-container__interests-textarea"
            placeholder="e.g., warm and welcoming, playful, professional..."
            rows="3"
          ></textarea>

          <div style="display: flex; align-items: center; gap: 10px; margin-top: 20px">
            <SrpButton class="init-step-container__interests-save-button" @click="saveCalendarConfig" :isDisabled="isSavingCalendarConfig">
              <template #icon>
                <LoadingIcon v-if="isSavingCalendarConfig" :size="18" />
                <IconEmbedded v-else name="check_3" :size="18" />
              </template>
              Save
            </SrpButton>
            <div v-if="isSavingCalendarConfig" class="save-msg">Saving...</div>
            <div v-else-if="justSavedCalendarConfig" class="save-msg">Saved!</div>
          </div>
        </div>

        <div class="init-step-container__divider"></div>
      </template>
    </template>

    <!-- Local Events -->
    <div v-if="isLoadingLocalEvents" style="margin-bottom: 50px; display: flex; flex-direction: column; justify-content: center; align-items: center">
      <LoaderNew style="width: 70px; height: 70px; margin-bottom: 10px" :borderWidth="3" />
      <div class="global-p" style="text-align: center">
        <h2>Generating Local Events</h2>
        We're generating Local Events for your content calendar!<br />
        This usually takes about a minute.<EllipsisAnimated />
      </div>
    </div>

    <!-- Header secondary -->
    <div class="header-secondary init-step-container__header-secondary init-step-container__header-secondary--with-toggle">
      <div>
        <h2 class="header-secondary__title">Local Events</h2>
        <div class="header-secondary__subtitle">We've scanned your area for events, add or adjust as needed.</div>
      </div>
      <SrpSwitch v-model:isChecked="showLocalEventsSection">{{ showLocalEventsSection ? "Enabled" : "Disabled" }}</SrpSwitch>
    </div>
    <!-- / Header secondary -->

    <div v-if="showLocalEventsSection" class="init-step-container__calendar-inputs-list-wrap">
      <ul class="init-step-container__calendar-inputs-list">
        <li
          :class="{
            'init-step-container__calendar-input-wrap': true,
            'init-step-container__calendar-input-wrap--drag-n-drop-ghost': elementDatasetDragged?.id === calendarInput.id,
            'init-step-container__calendar-input-wrap--drop-indicator-on-top':
              allElementDatasetsDraggedOver[0]?.id === calendarInput.id && [1, 2].includes(allElementDraggedOverAreaQuarters[0]) && elementDatasetDragged?.id !== calendarInput.id,
            'init-step-container__calendar-input-wrap--drop-indicator-on-bottom':
              allElementDatasetsDraggedOver[0]?.id === calendarInput.id && [3, 4].includes(allElementDraggedOverAreaQuarters[0]) && elementDatasetDragged?.id !== calendarInput.id,
          }"
          v-for="(calendarInput, index) in calendarInputsLocal"
          :key="calendarInput.id"
          :data-id="calendarInput.id"
          :data-index="index"
          :data-source="calendarInput.source"
        >
          <CalendarInputSnippet
            :class="{
              'init-step-container__calendar-input': true,
              'init-step-container__calendar-input--disabled': calendarInputWithEditModeId && calendarInputWithEditModeId !== calendarInput.id,
            }"
            :index="index"
            :calendarInput="calendarInput"
            :customerId="props.customerId"
            :showUrl="isDebugUI"
            @editModeSwitched="isEditMode => (calendarInputWithEditModeId = isEditMode ? calendarInput.id : '')"
            @saved="
              (date, title, description, source) => {
                handleCalendarInputSaved(calendarInput.id, date, title, description, source as ContentCalendarInputSource);
                MetricSender.sendMetric('Cust-Calendar-Theme-Local-Edit', { calendarInputId: calendarInput.id });
              }
            "
            @deleteRequested="id => openDeleteInputConfirm(id, 'local')"
            :isDisabled="Boolean(calendarInputWithEditModeId && calendarInputWithEditModeId !== calendarInput.id)"
          />
        </li>
      </ul>

      <SrpButton class="init-step-container__add-button" :isDisabled="Boolean(calendarInputWithEditModeId) || isLoadingGetCalendarInputsResponse" @click="isCreateLocalInputModalVisible = true">
        <template #icon><IconEmbedded name="plus_3" :size="22" /></template>
        Add Local Event
      </SrpButton>

      <CreateInputModal
        v-if="isCreateLocalInputModalVisible"
        v-model:isVisible="isCreateLocalInputModalVisible"
        @create="
          calendarInput => {
            handleCalendarInputCreated(calendarInput);
            MetricSender.sendMetric('Cust-Calendar-Theme-Local-Create', { calendarInputId: calendarInput.id });
          }
        "
        source="local"
        :biggestOrder="calendarInputsLocal[calendarInputsLocal.length - 1]?.order || 0"
      />
    </div>

    <div class="init-step-container__divider"></div>

    <!-- / Local Events -->

    <!-- Seasons & Holidays -->

    <div class="init-step-container__calendar-inputs-list-wrap">
      <!-- Header secondary -->
      <div class="header-secondary init-step-container__header-secondary init-step-container__header-secondary--with-toggle">
        <div>
          <h2 class="header-secondary__title">Seasons &amp; Holidays</h2>
          <div class="header-secondary__subtitle">A starter set of ready-to-use seasonal events to help you plan faster.</div>
        </div>
        <SrpSwitch v-model:isChecked="showSeasonsHolidaysSection">{{ showSeasonsHolidaysSection ? "Enabled" : "Disabled" }}</SrpSwitch>
      </div>
      <!-- / Header secondary -->
      <ul v-if="showSeasonsHolidaysSection" class="init-step-container__calendar-inputs-list">
        <li
          :class="{
            'init-step-container__calendar-input-wrap': true,
            'init-step-container__calendar-input-wrap--drag-n-drop-ghost': elementDatasetDragged?.id === calendarInput.id,
            'init-step-container__calendar-input-wrap--drop-indicator-on-top': isDragIntersectedOnTop(calendarInput.id),
            'init-step-container__calendar-input-wrap--drop-indicator-on-bottom': isDragIntersectedOnBottom(calendarInput.id),
          }"
          v-for="(calendarInput, index) in calendarInputsGeneral"
          :key="calendarInput.id"
          :data-id="calendarInput.id"
          :data-index="index"
          :data-source="calendarInput.source"
        >
          <CalendarInputSnippet
            class="init-step-container__calendar-input"
            :calendarInput="calendarInput"
            :customerId="props.customerId"
            :index="index"
            :showUrl="isDebugUI"
            @editModeSwitched="isEditMode => (calendarInputWithEditModeId = isEditMode ? calendarInput.id : '')"
            @saved="
              (date, title, description, source) => {
                handleCalendarInputSaved(calendarInput.id, date, title, description, source as ContentCalendarInputSource);
                MetricSender.sendMetric('Cust-Calendar-Theme-Holiday-Edit', { calendarInputId: calendarInput.id });
              }
            "
            @deleteRequested="id => openDeleteInputConfirm(id, 'template')"
            :isDisabled="Boolean(calendarInputWithEditModeId && calendarInputWithEditModeId !== calendarInput.id)"
          />
        </li>
      </ul>

      <SrpButton
        v-if="showSeasonsHolidaysSection"
        class="init-step-container__add-button"
        :isDisabled="Boolean(calendarInputWithEditModeId) || isLoadingGetCalendarInputsResponse"
        @click="isCreateTemplateInputModalVisible = true"
      >
        <template #icon><IconEmbedded name="plus_3" :size="22" /></template>
        Add Theme
      </SrpButton>

      <CreateInputModal
        v-if="isCreateTemplateInputModalVisible"
        v-model:isVisible="isCreateTemplateInputModalVisible"
        @create="
          calendarInput => {
            handleCalendarInputCreated(calendarInput);
            MetricSender.sendMetric('Cust-Calendar-Theme-Holiday-Create', { calendarInputId: calendarInput.id });
          }
        "
        source="template"
        :biggestOrder="calendarInputsGeneral[calendarInputsGeneral.length - 1]?.order || 0"
      />
    </div>
    <!-- / Seasons & Holidays -->

    <!-- What's Next
    <NoteWithIcon class="init-step-container__whats-next-note" size="small">
      <template #text>
        <div>
          <h5 class="global-h5">What's Next?</h5>
          <p class="global-p" style="text-wrap: auto">
            Topics will be generated to fill your content calendar (based on the themes above).<br />
            Posts will then be created from those Topics, which you can review and schedule!
          </p>
        </div>
      </template>
    </NoteWithIcon>
    / What's Next -->

    <!-- Buttons Panel -->
    <div class="init-step-container__buttons-panel">
      <SrpButton class="init-step-container__proceed-button" @click="emit('proceed')">
        <template #iconRight>
          <LoadingIcon v-if="isLoadingGetCalendarInputsResponse" :size="22" />
          <IconEmbedded v-else name="arrow-right_3-5" :size="22" />
        </template>
        Social Posts
      </SrpButton>
    </div>
    <!-- / Buttons Panel -->

    <!-- Super User Action Panel -->
    <div style="margin-top: 90px; width: 100%">
      <SuperUserActionPanel>
        <div>
          <div style="display: flex; flex-direction: row; gap: 8px">
            <SrpButton color="gray" fill="outlined" size="tiny" :isDisabled="isLoadingGetCalendarInputsResponse" @click="toggleShowDebugUI"> {{ isDebugUI ? "Hide" : "Show" }} Debug UI </SrpButton>
            <SrpButton color="gray" fill="outlined" size="tiny" :isDisabled="isLoadingGetCalendarInputsResponse || isRegenInProgress" @click="isRegenLocalConfirmVisible = true">
              <template #icon v-if="isRegenInProgress">
                <LoadingIcon :size="16" />
              </template>
              Re-gen Local
            </SrpButton>
            <SrpButton color="gray" fill="outlined" size="tiny" :isDisabled="isLoadingGetCalendarInputsResponse" @click="openLocalEventsPrompt"> Local Events Prompt </SrpButton>
            <SrpButton color="gray" fill="outlined" size="tiny" :isDisabled="isLoadingGetCalendarInputsResponse || isGenInterestsInProgress" @click="genInterestsAI">
              <template #icon v-if="isGenInterestsInProgress">
                <LoadingIcon :size="16" />
              </template>
              AI Gen Interests
            </SrpButton>
          </div>
          <div style="margin-top: 12px; text-align: left">
            <div class="global-p" style="margin: 0 0 6px; font-size: 13px; font-weight: 600">Bundle.social actions</div>
            <div style="display: flex; flex-direction: row; gap: 8px; flex-wrap: wrap">
              <SrpButton color="gray" fill="outlined" size="tiny" :isDisabled="isLoadingGetCalendarInputsResponse || isSyncingBundleStatus" @click="loadBundleAccounts"> Get Channels </SrpButton>
              <SrpButton color="gray" fill="outlined" size="tiny" :isDisabled="isLoadingGetCalendarInputsResponse || isSyncingBundleStatus" @click="syncBundleStatus">
                <template #icon v-if="isSyncingBundleStatus">
                  <LoadingIcon :size="16" />
                </template>
                Sync Status
              </SrpButton>
            </div>
          </div>
          <div v-if="facebookChannel || instagramChannel" style="margin-top: 12px; display: flex; flex-direction: column; gap: 4px; text-align: left">
            <span v-if="facebookChannel" class="global-p" style="margin: 0"><b>Facebook PageId:</b> {{ facebookChannel.channelId || "(none)" }}</span>
            <span v-if="instagramChannel" class="global-p" style="margin: 0"><b>Instagram Auth Mode:</b> {{ instagramChannel.instaAuthMode || "(not set)" }}</span>
          </div>
          <div style="text-align: left">
            <button type="button" class="website-social-discover__toggle" @click="isWebsiteSocialDiscoverOpen = !isWebsiteSocialDiscoverOpen">...</button>
            <div v-if="isWebsiteSocialDiscoverOpen" style="margin-top: 8px; display: flex; flex-direction: column; gap: 8px; max-width: 520px">
              <div style="display: flex; flex-direction: row; align-items: center; gap: 8px">
                <input
                  v-model="websiteSocialDiscoverUrl"
                  class="global-text-input"
                  type="text"
                  placeholder="https://example.com"
                  style="flex: 1; min-width: 0"
                  :disabled="isWebsiteSocialDiscoverLoading"
                />
                <SrpButton color="gray" fill="outlined" size="tiny" :isDisabled="isWebsiteSocialDiscoverLoading || !websiteSocialDiscoverUrl.trim()" @click="findSocialsFromWebsite">
                  <template #icon>
                    <LoadingIcon v-if="isWebsiteSocialDiscoverLoading" :size="16" />
                  </template>
                  Find socials
                </SrpButton>
              </div>
              <pre v-if="websiteSocialDiscoverResult" class="debug-pre">{{ websiteSocialDiscoverResult }}</pre>
            </div>
          </div>
          <div v-if="userSettings" style="margin-top: 12px; display: flex; flex-direction: row; align-items: center; gap: 10px; flex-wrap: wrap">
            <span class="global-p" style="margin: 0">Disable weekly gen</span>
            <SrpSwitch :isChecked="userSettings.disableWeeklyGen" :isDisabled="isSavingCalendarConfig || isLoadingGetCalendarInputsResponse" @change="onDisableWeeklyGenChange">
              {{ userSettings.disableWeeklyGen ? "Weekly gen is OFF" : "Weekly gen is enabled" }}
            </SrpSwitch>
          </div>
          <div style="margin-top: 16px; text-align: left">
            <h4>Calendar Context</h4>
            <textarea
              v-if="userSettings"
              id="super-user-calendar-context"
              v-model="userSettings.excludeFromIndex.customerContext"
              class="init-step-container__interests-textarea"
              placeholder="Special context for this calendar (ex. Resort guidance)"
              rows="4"
            ></textarea>
            <SrpButton fill="outlined" size="tiny" color="superonly" style="margin-top: 8px" :isDisabled="isSavingCalendarConfig" @click="saveCalendarConfig">
              <template #icon>
                <LoadingIcon v-if="isSavingCalendarConfig" :size="16" />
                <IconEmbedded v-else name="check_3" :size="16" />
              </template>
              Save
            </SrpButton>
          </div>
        </div>
        <pre class="debug-pre" v-if="bundleAccountsJson">{{ bundleAccountsJson }}</pre>
        <pre class="debug-pre" v-if="syncStatusJson">{{ syncStatusJson }}</pre>
      </SuperUserActionPanel>
    </div>
    <!-- / Super User Action Panel -->

    <div v-if="isDebugUI && formattedInterestsDebug" class="init-step-container__interests-debug">
      <h3>Interests Sources</h3>
      <pre style="background: #f5f5f5; padding: 12px; border-radius: 6px; font-size: 12px; overflow-x: auto; white-space: pre-wrap; word-wrap: break-word">{{ formattedInterestsDebug }}</pre>
    </div>

    <!-- Confirm Re-gen Local modal -->
    <SrpModal v-model:isVisible="isRegenLocalConfirmVisible" size="small">
      <template #header>
        <h2 class="global-h2">Re-generate Local Events?</h2>
      </template>
      <template #content> This will refresh local events for this customer. Continue? </template>
      <template #footer>
        <SrpButton color="gray" fill="outlined" style="margin-right: 10px" @click="isRegenLocalConfirmVisible = false" :isDisabled="isRegenInProgress">Cancel</SrpButton>
        <SrpButton
          @click="
            async () => {
              await regenEvents();
              isRegenLocalConfirmVisible = false;
            }
          "
          :isDisabled="isRegenInProgress"
        >
          <template #icon>
            <LoadingIcon v-if="isRegenInProgress" :size="20" />
          </template>
          Re-generate
        </SrpButton>
      </template>
    </SrpModal>

    <!-- Confirm Gen Interests modal -->
    <SrpModal v-model:isVisible="isGenInterestsConfirmVisible" size="small">
      <template #header>
        <h2 class="global-h2">Overwrite Existing Interests?</h2>
      </template>
      <template #content> Interests have already been set. Do you want to overwrite them with AI-generated interests? </template>
      <template #footer>
        <SrpButton color="gray" fill="outlined" style="margin-right: 10px" @click="isGenInterestsConfirmVisible = false" :isDisabled="isGenInterestsInProgress">Cancel</SrpButton>
        <SrpButton
          @click="
            async () => {
              await performGenInterests();
              isGenInterestsConfirmVisible = false;
            }
          "
          :isDisabled="isGenInterestsInProgress"
        >
          <template #icon>
            <LoadingIcon v-if="isGenInterestsInProgress" :size="20" />
          </template>
          Generate
        </SrpButton>
      </template>
    </SrpModal>

    <!-- Delete Event Confirmation Modal -->
    <SrpModal v-model:isVisible="isDeleteInputConfirmVisible" size="medium">
      <template #header>
        <h2 class="global-h2">Delete event?</h2>
      </template>
      <template #content>
        <p class="global-p" v-if="inputToDeleteTitle">
          Are you sure you want to delete <b>{{ inputToDeleteTitle }}</b
          ><span v-if="inputToDeleteDate"> ({{ inputToDeleteDate }})</span>?
        </p>
      </template>
      <template #footer>
        <SrpButton color="gray" fill="outlined" style="margin-right: 10px" @click="isDeleteInputConfirmVisible = false" :isDisabled="isDeletingInput">Cancel</SrpButton>
        <SrpButton color="red" @click="confirmDeleteInput" :isDisabled="isDeletingInput">
          <template #icon>
            <LoadingIcon v-if="isDeletingInput" :size="20" />
            <IconEmbedded v-else name="trashcan-alt_2" :size="20" />
          </template>
          Delete
        </SrpButton>
      </template>
    </SrpModal>

    <!-- Remove Slot Confirmation Modal -->
    <SrpModal v-model:isVisible="isRemoveSlotModalVisible" size="small">
      <template #header>
        <h2 class="global-h2">Remove Posting Day?</h2>
      </template>
      <template #content>
        <p class="global-p" v-if="slotToRemoveIndex !== null && slotsForCurrentPlan[slotToRemoveIndex]">
          Are you sure you want to remove <b>{{ getDayName(slotsForCurrentPlan[slotToRemoveIndex].day) }} at {{ slotsForCurrentPlan[slotToRemoveIndex].time }}</b
          >?
        </p>
      </template>
      <template #footer>
        <SrpButton color="gray" fill="outlined" style="margin-right: 10px" @click="isRemoveSlotModalVisible = false">Cancel</SrpButton>
        <SrpButton @click="confirmRemoveSlot">
          <template #icon>
            <IconEmbedded name="trashcan-alt_2" :size="20" />
          </template>
          Remove
        </SrpButton>
      </template>
    </SrpModal>

    <!-- Add Day Modal -->
    <SrpModal v-model:isVisible="isAddDayModalVisible" size="small">
      <template #header>
        <h2 class="global-h2">Add Posting Day</h2>
      </template>
      <template #content>
        <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 16px">
          <select class="simple-select" v-model.number="newSlotDay">
            <option v-for="d in daysOfWeek" :key="d.value" :value="d.value">{{ d.label }}</option>
          </select>
          <input class="global-text-input" v-model="newSlotTime" type="time" step="60" style="max-width: 120px" />
        </div>
        <div v-if="addDayErrorMessage" style="color: #d32f2f">{{ addDayErrorMessage }}</div>
      </template>
      <template #footer>
        <SrpButton color="gray" fill="outlined" style="margin-right: 10px" @click="isAddDayModalVisible = false">Cancel</SrpButton>
        <SrpButton @click="confirmAddSlot">
          <template #icon>
            <IconEmbedded name="plus_3" :size="20" />
          </template>
          Add Day
        </SrpButton>
      </template>
    </SrpModal>

    <SrpModal v-model:isVisible="isMoreSettingsModalVisible" size="medium">
      <template #header>
        <h2 class="global-h2">Atlas Settings</h2>
      </template>
      <template #content>
        <div>
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px">
            <IconEmbedded name="tag-person_2" :size="22" color="#5b5b5b" />
            <h3 class="global-h4" style="margin: 0">Tagging Settings</h3>
          </div>
          <p class="global-p" style="margin-bottom: 12px">
            New posts will automatically tag and mention attached locations.<br />If disabled, the location stays attached but won't be tagged or mentioned.
          </p>
          <p class="global-p" style="margin-bottom: 12px"><i>Note:</i> Existing posts are not altered (no tags added or removed) when this is toggled.</p>
          <div style="display: flex; align-items: center; gap: 10px">
            <span style="font-weight: 600">{{ isAutoTaggingDisabled ? "Auto-tagging is Disabled" : "Auto-tagging is Enabled" }}</span>
            <LinkWithIcon isDottedUnderline color="blue" :isDisabled="isSavingCalendarConfig" @click="toggleAutoTagging">{{ isAutoTaggingDisabled ? "Enable" : "Disable" }}</LinkWithIcon>
          </div>
        </div>

        <div style="margin: 22px 0; border-bottom: 1px solid rgba(0, 0, 0, 0.15)"></div>

        <div>
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px">
            <IconEmbedded name="image_2" :size="20" color="#5b5b5b" />
            <h3 class="global-h4" style="margin: 0">Aspect Ratio Limits</h3>
          </div>
          <p class="global-p" style="white-space: pre-line; margin-bottom: 10px">{{ aspectRatioLimitsDescription }}</p>
          <div>
            You can
            <LinkWithIcon isDottedUnderline color="blue" :isDisabled="isSavingCalendarConfig" @click="toggleCalendarImageFit">{{ imageFitToggleLabel }}</LinkWithIcon>
            instead of {{ imageFitCurrentNoun }}.
          </div>
          <p class="global-p" style="margin-top: 12px"><i>Note:</i> Approved posts are not affected (but unapproved posts are) when this is toggled.</p>
        </div>
      </template>
      <template #footer>
        <SrpButton @click="saveAtlasSettings" :isDisabled="isSavingCalendarConfig">
          <template #icon>
            <LoadingIcon v-if="isSavingCalendarConfig" :size="18" />
            <IconEmbedded v-else name="check_3" :size="18" />
          </template>
          Save
        </SrpButton>
      </template>
    </SrpModal>

    <SocialAccountConnectPrep
      v-model:isVisible="isSocialAccountConnectPrepVisible"
      :customerId="props.customerId"
      :isRedirecting="isRedirectingToPortal"
      :connectedPlatforms="connectedPlatformChannelTypes"
      @confirm="linkMoreAccounts"
    />

    <NewSocialAccountAddedModal
      :customerId="props.customerId"
      :pendingChannels="systemSettings?.pendingChannels"
      :socialChannels="socialChannels"
      @applied="handlePendingChannelsApplied"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { MetricSender } from "@helpers/MetricSender";

// Components
import CalendarInputSnippet from "../eventSnippets/CalendarInputSnippet.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import Loader from "@components/Loader/Loader.vue";
import CreateInputModal from "./modals/CreateInputModal.vue";
import HowContentCalendarWorksSpoiler from "../HowContentCalendarWorksSpoiler.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import { ATLAS_SETTINGS_HASH, LOCATIONS_SECTION_HASH, SYNC_ACCOUNTS_KEY, clearSyncAccountsHash } from "@logic/ContentCalendarHelper";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import SrpSwitch from "@components/ui/SrpSwitch.vue";
import SocialMediaSettingsRepo from "@repos/SocialMediaSettingsRepo";
import SocialIntegrationRepo from "@repos/SocialIntegrationRepo";
import InfoIconWithTooltip from "@/components/ui/InfoIconWithTooltip.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import CalendarPicker from "@components/CalendarPicker.vue";
import RequestCalendarLink from "../RequestCalendarLink.vue";
import SocialAccountConnectPrep from "../SocialAccountConnectPrep.vue";
import SavedLocationsSection from "@components/SocialLocations/SavedLocationsSection.vue";
import NewSocialAccountAddedModal from "./modals/NewSocialAccountAddedModal.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import { isCurrentCalendarTest, getShowTestCalendars, getShowDebugUI, setShowDebugUI } from "@logic/calendarManager";
import { isChannelAuthNeeded } from "@logic/SocialMediaPost";

// Types
import { ContentCalendarInput, ContentCalendarInputSource, GetCalendarInputsResponse } from "@contracts/contentCalendar";
import {
  COMMON_US_TIMEZONES,
  cloneUserSettingsForEditing,
  type ApplyPendingChannelsResponse,
  type ContentCalendarSystemSettingsData,
  type ContentCalendarUserSettingsData,
  type SocialMediaSettingsV2Response,
} from "@/contracts/socialMediaSettings";
import { SocialChannelStatus, SocialImageFitMode } from "@contracts/shareStop";
import { getMetaFeedAspectRatioLimitsDescription, resolveSocialImageFitMode } from "@logic/AtlasSchedulingValidation";
import type { IconName } from "@/contracts/IconName";
import { channelProfileUrl } from "@logic/SocialFieldInputCleaner";

// Composables
import { AreaQuarter, useMouseDragNDrop } from "@composables/useMouseDragNDrop";
import { useTouchDragNDrop } from "@composables/useTouchDragNDrop";
import EllipsisAnimated from "@components/ui/EllipsisAnimated.vue";
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";

const props = defineProps<{
  customerId: string;
}>();

const emit = defineEmits<{
  (e: "proceed"): void;
}>();

// Load calendar inputs state =================================================
const getCalendarInputsResponse = ref<GetCalendarInputsResponse | null>(null);
const isLoadingGetCalendarInputsResponse = ref<boolean>(true);
const isLoadingLocalEvents = ref<boolean>(false);
const isLoadingInterests = ref<boolean>(false);

// DOM refs ===================================================================
const domRefRoot = ref<HTMLElement | null>(null);

// Toggle the Create Input modals =============================================
const isCreateTemplateInputModalVisible = ref<boolean>(false);
const isCreateLocalInputModalVisible = ref<boolean>(false);

// Split inputs array by type and sort ========================================
const calendarInputsGeneral = ref<Array<ContentCalendarInput>>([]);
const calendarInputsLocal = ref<Array<ContentCalendarInput>>([]);

function sortInputsByOrderField(a, b) {
  if (a.order === null && b.order == null) return 0;
  if (a.order === null) return -1;
  if (b.order === null) return 1;
  return a.order - b.order;
}

function splitInputsBySourceAndSort() {
  const inputs = getCalendarInputsResponse.value?.inputs || [];
  calendarInputsGeneral.value = inputs.filter(i => i.source === "template").sort(sortInputsByOrderField);
  calendarInputsLocal.value = inputs.filter(i => i.source === "local").sort(sortInputsByOrderField);
}

onMounted(async () => {
  isCalendarTestMode.value = await isCurrentCalendarTest(props.customerId);
  await loadSocialMediaSettings();
  await loadCalendarCustomerInputList(); // Call this after settings above since the gen operation can be quite slow
  splitInputsBySourceAndSort();
  await applyConfigHashActions();
});
watch(() => getCalendarInputsResponse.value?.inputs, splitInputsBySourceAndSort);

async function applyConfigHashActions() {
  const hash = globalThis.location.hash.replace(/^#/, "");
  if (hash === ATLAS_SETTINGS_HASH) {
    isMoreSettingsModalVisible.value = true;
  }
  if (hash === LOCATIONS_SECTION_HASH) {
    await nextTick();
    document.getElementById(LOCATIONS_SECTION_HASH)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

// Track which Calendar input snippet is in the Edit mode =====================
const calendarInputWithEditModeId = ref<string>("");

// Handlers for calendar input events =========================================
function handleCalendarInputSaved(calendarInputId: string, date: string, title: string, description: string, source: ContentCalendarInputSource) {
  if (!getCalendarInputsResponse.value) return;
  getCalendarInputsResponse.value.inputs = getCalendarInputsResponse.value.inputs.map(t => (t.id === calendarInputId ? { ...t, date, title, description, source } : t));
}

function handleCalendarInputDeleted(id: string) {
  if (!getCalendarInputsResponse.value) return;
  getCalendarInputsResponse.value.inputs = getCalendarInputsResponse.value.inputs.filter(t => t.id !== id);
}

const isDeleteInputConfirmVisible = ref<boolean>(false);
const inputToDeleteId = ref<string>("");
const inputToDeleteTitle = ref<string>("");
const inputToDeleteDate = ref<string>("");
const inputToDeleteSource = ref<ContentCalendarInputSource | null>(null);
const isDeletingInput = ref<boolean>(false);

function openDeleteInputConfirm(id: string, source: ContentCalendarInputSource) {
  const input = getCalendarInputsResponse.value?.inputs.find(i => i.id === id);
  inputToDeleteId.value = id;
  inputToDeleteTitle.value = input?.title ?? "this event";
  inputToDeleteDate.value = input?.date ?? "";
  inputToDeleteSource.value = source;
  isDeleteInputConfirmVisible.value = true;
}

async function confirmDeleteInput() {
  if (!inputToDeleteId.value || isDeletingInput.value) return;

  isDeletingInput.value = true;
  await ContentCalendarRepo.deleteInput(props.customerId, inputToDeleteId.value);
  handleCalendarInputDeleted(inputToDeleteId.value);

  if (inputToDeleteSource.value === "local") {
    MetricSender.sendMetric("Cust-Calendar-Theme-Local-Delete", { calendarInputId: inputToDeleteId.value });
  } else {
    MetricSender.sendMetric("Cust-Calendar-Theme-Holiday-Delete", { calendarInputId: inputToDeleteId.value });
  }

  isDeletingInput.value = false;
  isDeleteInputConfirmVisible.value = false;
  inputToDeleteId.value = "";
  inputToDeleteTitle.value = "";
  inputToDeleteDate.value = "";
  inputToDeleteSource.value = null;
}

function handleCalendarInputCreated(calendarInput: ContentCalendarInput) {
  if (!getCalendarInputsResponse.value) return;
  getCalendarInputsResponse.value.inputs = [...getCalendarInputsResponse.value.inputs, calendarInput];
}

// Open Local Events Prompt in a new window ====================================
const router = useRouter();

function openLocalEventsPrompt() {
  const routeLocation = router.resolve({ name: "ConfigAdmin", query: { customerId: props.customerId, localEvents: "true" }, hash: "#cal-local-events" });
  const href = routeLocation.href.startsWith("http") ? routeLocation.href : `${window.location.origin}${routeLocation.href}`;
  window.open(href, "_blank", "noopener,noreferrer");
}

// Load calendar customer inputs ==============================================
async function loadCalendarCustomerInputList() {
  isLoadingGetCalendarInputsResponse.value = true;
  getCalendarInputsResponse.value = await ContentCalendarRepo.getAllInputsForCustomer(props.customerId, true, false);
  isLoadingGetCalendarInputsResponse.value = false;

  if (getCalendarInputsResponse.value.shouldGenInterests) {
    isLoadingInterests.value = true;
    await ContentCalendarRepo.genInterests(props.customerId, true, false);
    await loadSocialMediaSettings();
    isLoadingInterests.value = false;
  }

  if (getCalendarInputsResponse.value.shouldCreateLocalEvents) {
    isLoadingLocalEvents.value = true;
    const localEvents: Array<ContentCalendarInput> = await ContentCalendarRepo.genLocalEvents(props.customerId);
    getCalendarInputsResponse.value.inputs = [...getCalendarInputsResponse.value.inputs, ...localEvents];
    isLoadingLocalEvents.value = false;
  }
}

// Regen Events (Local)
const isRegenInProgress = ref<boolean>(false);
const isRegenLocalConfirmVisible = ref<boolean>(false);

async function regenEvents() {
  if (isRegenInProgress.value) return;
  isRegenInProgress.value = true;
  await ContentCalendarRepo.genLocalEvents(props.customerId, true);
  isRegenInProgress.value = false;
  loadCalendarCustomerInputList();
}

// Gen Interests with AI
const isGenInterestsInProgress = ref<boolean>(false);
const isGenInterestsConfirmVisible = ref<boolean>(false);

async function genInterestsAI() {
  const existing = userSettings.value?.excludeFromIndex?.interests;
  if (existing && existing.trim().length > 0) {
    isGenInterestsConfirmVisible.value = true;
    return;
  }

  await performGenInterests();
}

async function performGenInterests() {
  if (isGenInterestsInProgress.value) return;
  isGenInterestsInProgress.value = true;
  await ContentCalendarRepo.genInterests(props.customerId, true, true);
  await loadSocialMediaSettings();
  isGenInterestsInProgress.value = false;
}

// Debug UI toggle ==============================================================
const isDebugUI = ref<boolean>(getShowDebugUI());

function toggleShowDebugUI() {
  const newValue = !isDebugUI.value;
  isDebugUI.value = newValue;
  setShowDebugUI(newValue);
}

// Calendar test mode ===========================================================
const isCalendarTestMode = ref<boolean>(false);

// Section visibility toggles ===================================================
const showInterestsSection = ref<boolean>(true);
const showLocalEventsSection = ref<boolean>(true);
const showSeasonsHolidaysSection = ref<boolean>(true);
const showCalendarName = ref<boolean>(false);
const isMoreSettingsModalVisible = ref(false);
const isSavingCalendarConfig = ref<boolean>(false);
const justSavedCalendarConfig = ref(false);
const systemSettings = ref<ContentCalendarSystemSettingsData | null>(null);
const userSettings = ref<ContentCalendarUserSettingsData | null>(null);
const timeZone = ref<string | null>(null);

// Social Settings (moved from SocialSettings.vue) ================================
const socialChannels = computed(() => {
  const list = systemSettings.value?.socialChannels || [];
  return list.filter(ch => ch.status !== SocialChannelStatus.Deleted);
});
const facebookChannel = computed(() => socialChannels.value.find(ch => ch.channelType === "facebook") ?? null);
const instagramChannel = computed(() => socialChannels.value.find(ch => ch.channelType === "instagram") ?? null);
const connectedPlatformChannelTypes = computed(() => socialChannels.value.filter(ch => ch.status === SocialChannelStatus.Connected).map(ch => ch.channelType));
const authNeededChannels = computed(() => socialChannels.value.filter(ch => isChannelAuthNeeded(ch.status)));

const MEDIA_PER_POST_MIN = 1;
const MEDIA_PER_POST_MAX = 7;
const POSTS_PER_WEEK_MIN = 1;
const POSTS_PER_WEEK_MAX = 3;
const WEEKS_TO_GEN_MIN = 1;
const WEEKS_TO_GEN_MAX = 4;

const shouldSyncOnLoad = computed(() => globalThis.location.hash === "#" + SYNC_ACCOUNTS_KEY);

const daysOfWeek = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const isSavingSocialSettings = ref(false);
const justSavedSocial = ref(false);

// Modal states for add/remove slots
const isRemoveSlotModalVisible = ref(false);
const slotToRemoveIndex = ref<number | null>(null);
const isAddDayModalVisible = ref(false);
const newSlotDay = ref<number>(1); // Default to Monday
const newSlotTime = ref<string>("10:00");
const addDayErrorMessage = ref<string>("");

const slotsForCurrentPlan = computed(() => {
  if (!userSettings.value) return [] as Array<{ day: number; time: string }>;
  return userSettings.value.postingSchedule.schedule || [];
});

const postsPerWeekMessage = computed(() => {
  const count = slotsForCurrentPlan.value.length;
  return `${count} post${count !== 1 ? "s" : ""} will generate each week.`;
});

// Super-user bundle accounts debug
const bundleAccountsJson = ref("");
const syncStatusJson = ref("");
const isSyncingBundleStatus = ref(false);

// Super-user website social discovery POC
const isWebsiteSocialDiscoverOpen = ref(false);
const websiteSocialDiscoverUrl = ref("");
const websiteSocialDiscoverResult = ref("");
const isWebsiteSocialDiscoverLoading = ref(false);

// Redirect to portal state
const isRedirectingToPortal = ref(false);
const isSocialAccountConnectPrepVisible = ref(false);

// Format interests debug JSON for display
const formattedInterestsDebug = computed(() => {
  if (!userSettings.value?.excludeFromIndex?.interestsDebug) return null;
  try {
    const parsed = JSON.parse(userSettings.value.excludeFromIndex.interestsDebug);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return userSettings.value.excludeFromIndex.interestsDebug;
  }
});

function applyV2Response(v2: SocialMediaSettingsV2Response) {
  systemSettings.value = v2.settings;
  userSettings.value = cloneUserSettingsForEditing(v2.userSettings);
  timeZone.value = v2.timeZone;
  showInterestsSection.value = userSettings.value?.enableInterests ?? true;
  showLocalEventsSection.value = userSettings.value?.enableLocalEvents ?? true;
  showSeasonsHolidaysSection.value = userSettings.value?.enableThemes ?? true;
}

function handlePendingChannelsApplied(response: ApplyPendingChannelsResponse) {
  systemSettings.value = response.settings;
  if (response.userSettings) {
    userSettings.value = cloneUserSettingsForEditing(response.userSettings);
  }
}

async function loadSocialMediaSettings() {
  if (shouldSyncOnLoad.value) {
    try {
      await SocialMediaSettingsRepo.checkForLinkedAccount(props.customerId);
    } catch (err) {
      getGlobalRemoteLogger()?.error(`Failed to check for linked account for ${props.customerId}: ${err}`);
    } finally {
      clearSyncAccountsHash();
    }
  }

  const v2Response = await SocialMediaSettingsRepo.getSettings(props.customerId);
  applyV2Response(v2Response);

  // Check if multiple calendars exist to show calendar name section
  const includeTest = getShowTestCalendars();
  const calendars = await ContentCalendarRepo.getCalendarsForCustomer(props.customerId, includeTest);
  showCalendarName.value = calendars.length > 1;
}

const calendarImageFitMode = computed(() => resolveSocialImageFitMode(userSettings.value?.imageFit, null));
const isCalendarImageFitPad = computed(() => calendarImageFitMode.value === SocialImageFitMode.Pad);
const aspectRatioLimitsDescription = computed(() => getMetaFeedAspectRatioLimitsDescription(calendarImageFitMode.value));
const imageFitToggleLabel = computed(() => (isCalendarImageFitPad.value ? "use cropping" : "use padding"));
const imageFitCurrentNoun = computed(() => (isCalendarImageFitPad.value ? "padding" : "cropping"));
const isAutoTaggingDisabled = computed(() => userSettings.value?.disableAutoTagging ?? false);

function toggleCalendarImageFit() {
  if (!userSettings.value || isSavingCalendarConfig.value) return;
  userSettings.value.imageFit = { mode: isCalendarImageFitPad.value ? SocialImageFitMode.Crop : SocialImageFitMode.Pad };
}

async function saveAtlasSettings() {
  await saveCalendarConfig();
  isMoreSettingsModalVisible.value = false;
}

async function saveCalendarConfig() {
  isSavingCalendarConfig.value = true;
  try {
    if (userSettings.value) {
      const v2Response = await SocialMediaSettingsRepo.updateSettings(userSettings.value, timeZone.value);
      applyV2Response(v2Response);
      justSavedCalendarConfig.value = true;
      setTimeout(() => (justSavedCalendarConfig.value = false), 2000);
    }
  } finally {
    isSavingCalendarConfig.value = false;
  }
}

async function onDisableWeeklyGenChange(ev: Event) {
  const input = ev.target as HTMLInputElement | null;
  if (!input || !userSettings.value || isSavingCalendarConfig.value) return;
  userSettings.value.disableWeeklyGen = input.checked;
  await saveCalendarConfig();
}

function toggleAutoTagging() {
  if (!userSettings.value || isSavingCalendarConfig.value) return;
  userSettings.value.disableAutoTagging = !isAutoTaggingDisabled.value;
}

async function updateSectionVisibility(field: "enableInterests" | "enableLocalEvents" | "enableThemes", value: boolean) {
  if (!userSettings.value) return;

  userSettings.value[field] = value;
  const v2Response = await SocialMediaSettingsRepo.updateSettings(userSettings.value, timeZone.value);
  applyV2Response(v2Response);
}

// Social Settings functions =====================================================
function channelToIcon(channelType: string): IconName {
  switch (channelType) {
    case "facebook":
      return "facebook";
    case "instagram":
      return "instagram";
    case "twitter":
      return "xwitter";
    case "linkedin":
      return "linkedin";
    case "tiktok":
      return "tiktok";
    case "youtube":
      return "youtube";
    default:
      return "share-up_2";
  }
}

function openSocialAccountConnectPrepModal() {
  isSocialAccountConnectPrepVisible.value = true;
}

async function linkMoreAccounts(payload: { selectedPlatforms: Array<string>; forceStandardScope?: boolean }) {
  if (isRedirectingToPortal.value) return;
  isRedirectingToPortal.value = true;
  MetricSender.sendMetric("Cust-Calendar-Connect-Accounts", { customerId: props.customerId, hasChannels: socialChannels.value?.length > 0, page: "config" });
  const returnUrl = `${globalThis.location.origin}${globalThis.location.pathname}${globalThis.location.search}#${SYNC_ACCOUNTS_KEY}`;
  try {
    const portalUrl = await SocialIntegrationRepo.createPortalLink(props.customerId, returnUrl, payload.selectedPlatforms, {
      forceStandardScope: payload.forceStandardScope === true,
    });
    globalThis.location.href = portalUrl;
  } catch (err) {
    isRedirectingToPortal.value = false;
    getGlobalRemoteLogger()?.error(`Failed to create portal link for ${props.customerId}: ${err}`);
  }
}

async function saveSocialSettings() {
  if (!userSettings.value) return;
  MetricSender.sendMetric("Cust-Calendar-Settings-Save", {
    customerId: props.customerId,
    postsPerWeek: userSettings.value?.postsPerWeek,
  });
  isSavingSocialSettings.value = true;

  const schedule = (userSettings.value.postingSchedule.schedule || []).map(s => ({
    day: s.day,
    time: s.time.length === 5 ? `${s.time}:00` : s.time,
  }));

  userSettings.value.postingSchedule = { schedule };

  const v2Response = await SocialMediaSettingsRepo.updateSettings(userSettings.value, timeZone.value);
  applyV2Response(v2Response);
  justSavedSocial.value = true;
  setTimeout(() => (justSavedSocial.value = false), 2000);
  isSavingSocialSettings.value = false;
}

// Sort slots by day (Mon-Sun) then by time
function sortSlots(slots: Array<{ day: number; time: string }>): Array<{ day: number; time: string }> {
  return [...slots].sort((a, b) => {
    // Treat Sunday (0) as 7 for sorting purposes (end of week)
    const dayA = a.day === 0 ? 7 : a.day;
    const dayB = b.day === 0 ? 7 : b.day;

    if (dayA !== dayB) {
      return dayA - dayB;
    }

    // Same day, sort by time
    return a.time.localeCompare(b.time);
  });
}

// Get day name from day number
function getDayName(day: number): string {
  const dayObj = daysOfWeek.find(d => d.value === day);
  return dayObj ? dayObj.label : String(day);
}

// Open remove slot modal
function openRemoveSlotModal(index: number) {
  slotToRemoveIndex.value = index;
  isRemoveSlotModalVisible.value = true;
}

// Confirm remove slot
function confirmRemoveSlot() {
  if (!userSettings.value || slotToRemoveIndex.value === null) return;

  // Remove the slot
  const slots = [...slotsForCurrentPlan.value];
  slots.splice(slotToRemoveIndex.value, 1);

  // Update the schedule
  userSettings.value.postingSchedule.schedule = slots;
  userSettings.value.postsPerWeek = slots.length;

  isRemoveSlotModalVisible.value = false;
  slotToRemoveIndex.value = null;

  MetricSender.sendMetric("Cust-Calendar-Settings-RemoveSlot", { customerId: props.customerId });
}

// Confirm add slot
function confirmAddSlot() {
  if (!userSettings.value) return;

  addDayErrorMessage.value = "";

  // Validate: check for duplicate
  const isDuplicate = slotsForCurrentPlan.value.some(slot => slot.day === newSlotDay.value && slot.time === newSlotTime.value);

  if (isDuplicate) {
    addDayErrorMessage.value = "This day and time combination already exists.";
    return;
  }

  // Add the new slot
  const newSlot = { day: newSlotDay.value, time: newSlotTime.value };
  const slots = [...slotsForCurrentPlan.value, newSlot];

  // Sort the slots
  const sortedSlots = sortSlots(slots);

  // Update the schedule
  userSettings.value.postingSchedule.schedule = sortedSlots;
  userSettings.value.postsPerWeek = sortedSlots.length;

  isAddDayModalVisible.value = false;

  // Reset form
  newSlotDay.value = 1;
  newSlotTime.value = "10:00";
  addDayErrorMessage.value = "";

  MetricSender.sendMetric("Cust-Calendar-Settings-AddSlot", { customerId: props.customerId });
}

async function findSocialsFromWebsite() {
  const websiteUrl = websiteSocialDiscoverUrl.value.trim();
  if (!websiteUrl || isWebsiteSocialDiscoverLoading.value) return;
  isWebsiteSocialDiscoverLoading.value = true;
  websiteSocialDiscoverResult.value = "";
  try {
    const result = await SocialLocationsRepo.discoverSocialsFromWebsite(props.customerId, websiteUrl);
    websiteSocialDiscoverResult.value = JSON.stringify(result, null, 2);
  } catch (err: any) {
    const data = err?.response?.data;
    const message = typeof data === "string" ? data : data?.detail ?? data?.title ?? err?.message ?? String(err);
    websiteSocialDiscoverResult.value = `Error: ${message}`;
    getGlobalRemoteLogger()?.error(`Website social discovery failed for ${props.customerId}: ${err}`);
  } finally {
    isWebsiteSocialDiscoverLoading.value = false;
  }
}

async function loadBundleAccounts() {
  const result = await SocialIntegrationRepo.getSocialAccounts(props.customerId);
  bundleAccountsJson.value = result;
}

async function syncBundleStatus() {
  if (isSyncingBundleStatus.value) return;
  isSyncingBundleStatus.value = true;
  syncStatusJson.value = "";
  try {
    const result = await SocialIntegrationRepo.syncAuthNeededStatus(props.customerId, true, false);
    syncStatusJson.value = JSON.stringify(result, null, 2);
    const v2Response = await SocialMediaSettingsRepo.getSettings(props.customerId);
    applyV2Response(v2Response);
  } catch (err: any) {
    const data = err?.response?.data;
    const message = typeof data === "string" ? data : data?.detail ?? data?.title ?? err?.message ?? String(err);
    syncStatusJson.value = `Error: ${message}`;
    getGlobalRemoteLogger()?.error(`Sync Bundle status failed for ${props.customerId}: ${err}`);
  } finally {
    isSyncingBundleStatus.value = false;
  }
}

watch(showInterestsSection, newValue => {
  updateSectionVisibility("enableInterests", newValue);
});

watch(showLocalEventsSection, newValue => {
  updateSectionVisibility("enableLocalEvents", newValue);
});

watch(showSeasonsHolidaysSection, newValue => {
  updateSectionVisibility("enableThemes", newValue);
});

// Watchers for social settings values ==========================================
watch(
  () => userSettings.value?.mediaPerPost,
  newVal => {
    if (!userSettings.value) return;
    const num = Number(newVal ?? 0);
    if (!Number.isFinite(num)) {
      userSettings.value.mediaPerPost = MEDIA_PER_POST_MIN;
      return;
    }
    if (num < MEDIA_PER_POST_MIN) userSettings.value.mediaPerPost = MEDIA_PER_POST_MIN;
    else if (num > MEDIA_PER_POST_MAX) userSettings.value.mediaPerPost = MEDIA_PER_POST_MAX;
  }
);

// Watch slots to keep postsPerWeek in sync
watch(
  () => userSettings.value?.postingSchedule.schedule?.length,
  newLength => {
    if (!userSettings.value || newLength === undefined) return;
    // Sync postsPerWeek with the actual number of slots
    userSettings.value.postsPerWeek = newLength;
  }
);

watch(
  () => userSettings.value?.postsPerWeek,
  newVal => {
    if (!userSettings.value) return;
    const num = Number(newVal ?? 0);
    if (!Number.isFinite(num)) {
      userSettings.value.postsPerWeek = POSTS_PER_WEEK_MIN;
    } else if (num < POSTS_PER_WEEK_MIN) {
      userSettings.value.postsPerWeek = POSTS_PER_WEEK_MIN;
    } else if (num > POSTS_PER_WEEK_MAX) {
      userSettings.value.postsPerWeek = POSTS_PER_WEEK_MAX;
    }
  }
);

watch(
  () => userSettings.value?.weeksToGenPosts,
  newVal => {
    if (!userSettings.value) return;
    const num = Number(newVal ?? 0);
    if (!Number.isFinite(num) || num < WEEKS_TO_GEN_MIN) {
      userSettings.value.weeksToGenPosts = WEEKS_TO_GEN_MIN;
    } else if (num > WEEKS_TO_GEN_MAX) {
      userSettings.value.weeksToGenPosts = WEEKS_TO_GEN_MAX;
    }
  }
);

// Drag&Drop ==================================================================
const dragAndDropRecalcKey = computed(() => {
  const inputs = getCalendarInputsResponse.value?.inputs || [];
  return inputs.map(i => i.id + i.source).join() + calendarInputWithEditModeId.value;
});

const { isMouseDragInProgress, elementDatasetMouseDragged, allElementDatasetsMouseDraggedOver, allElementMouseDraggedOverAreaQuarters } = useMouseDragNDrop(
  ref(true),
  dragAndDropRecalcKey,
  domRefRoot,
  ".init-step-container__calendar-input-wrap",
  "*[data-drag-n-drop-handle]",
  handleElementMove,
  10,
  { x: 840, y: 35 },
  ""
);
const { isTouchDragInProgress, elementDatasetTouchDragged, allElementDatasetsTouchDraggedOver, allElementTouchDraggedOverAreaQuarters } = useTouchDragNDrop(
  ref(true),
  dragAndDropRecalcKey,
  domRefRoot,
  ".init-step-container__calendar-input-wrap",
  "*[data-drag-n-drop-handle]",
  handleElementMove,
  10,
  { x: 840, y: 35 },
  ""
);

const elementDatasetDragged = computed<DOMStringMap | null>(() => {
  if (isMouseDragInProgress.value) return elementDatasetMouseDragged.value;
  if (isTouchDragInProgress.value) return elementDatasetTouchDragged.value;
  return null;
});
const allElementDatasetsDraggedOver = computed<Array<DOMStringMap>>(() => {
  if (isMouseDragInProgress.value) return allElementDatasetsMouseDraggedOver.value;
  if (isTouchDragInProgress.value) return allElementDatasetsTouchDraggedOver.value;
  return [];
});
const allElementDraggedOverAreaQuarters = computed<Array<AreaQuarter>>(() => {
  if (isMouseDragInProgress.value) return allElementMouseDraggedOverAreaQuarters.value;
  if (isTouchDragInProgress.value) return allElementTouchDraggedOverAreaQuarters.value;
  return [];
});

function isDragIntersectedOnTop(id: string) {
  return allElementDatasetsDraggedOver.value?.[0]?.id === id && [1, 2].includes(allElementDraggedOverAreaQuarters.value?.[0]) && elementDatasetDragged.value?.id !== id;
}
function isDragIntersectedOnBottom(id: string) {
  return allElementDatasetsDraggedOver.value?.[0]?.id === id && [3, 4].includes(allElementDraggedOverAreaQuarters.value?.[0]) && elementDatasetDragged.value?.id !== id;
}

// Moves the element and saves new order values ===============================
const isSavingNewOrderAndSourceValues = ref<boolean>(false);

async function handleElementMove(...args) {
  const argsObj = args.reverse()[0];

  const draggedDataset = argsObj.elementDatasetDragged || argsObj.elementDatasetTouchDragged;
  const dragoverDataset = argsObj.allElementDatasetsDraggedOver?.[0] || argsObj.allElementDatasetsTouchDraggedOver?.[0];
  const dragoverAreaQuarter = argsObj.allElementDraggedOverAreaQuarters?.[0] || argsObj.allElementTouchDraggedOverAreaQuarters?.[0];

  // If dropped on itself
  if (draggedDataset.source === dragoverDataset.source && draggedDataset.id === dragoverDataset.id) {
    return;
  }

  // If dropped on the bottom half of the previous elem or top half of the next elem
  if (draggedDataset.source === dragoverDataset.source) {
    const isDroppedOnBottomHalfOfPrevElem = +draggedDataset.index - 1 === +dragoverDataset.index && [3, 4].includes(+dragoverAreaQuarter);
    const isDroppedOnTopHalfOfNextElem = +draggedDataset.index + 1 === +dragoverDataset.index && [1, 2].includes(+dragoverAreaQuarter);
    if (isDroppedOnBottomHalfOfPrevElem || isDroppedOnTopHalfOfNextElem) {
      return;
    }
  }

  // Turn every elem into an array to avoid placing the elem in a wrong spot
  // (because the index shifts when the element deleting from the previous index, and it's hard to compensate for that shift precisely)
  const newCalendarInputsGeneral = calendarInputsGeneral.value.map(v => [v]);
  const newCalendarInputsLocal = calendarInputsLocal.value.map(v => [v]);

  let itemToMove: ContentCalendarInput | null = null;
  if (draggedDataset.source === "template") {
    itemToMove = calendarInputsGeneral.value[+draggedDataset.index];
    newCalendarInputsGeneral[+draggedDataset.index] = [];
  } else if (draggedDataset.source === "local") {
    itemToMove = calendarInputsLocal.value[+draggedDataset.index];
    newCalendarInputsLocal[+draggedDataset.index] = [];
  }

  itemToMove.source = dragoverDataset.source;

  if (dragoverDataset.source === "template") {
    if ([1, 2].includes(+dragoverAreaQuarter)) newCalendarInputsGeneral[+dragoverDataset.index].unshift(itemToMove);
    else newCalendarInputsGeneral[+dragoverDataset.index].push(itemToMove);
  } else if (dragoverDataset.source === "local") {
    if ([1, 2].includes(+dragoverAreaQuarter)) newCalendarInputsLocal[+dragoverDataset.index].unshift(itemToMove);
    else newCalendarInputsLocal[+dragoverDataset.index].push(itemToMove);
  }

  calendarInputsGeneral.value = newCalendarInputsGeneral.flat();
  calendarInputsLocal.value = newCalendarInputsLocal.flat();

  // Change the Order field of the "item to move"
  let targetArray = dragoverDataset.source === "template" ? calendarInputsGeneral.value : calendarInputsLocal.value;

  const itemIndex = targetArray.findIndex(i => i === itemToMove);

  const prevOrder = targetArray[itemIndex - 1]?.order || 0;
  const nextOrder = targetArray[itemIndex + 1]?.order || 0;

  itemToMove.order = prevOrder > 0 ? (nextOrder - prevOrder) / 2 + prevOrder : nextOrder / 2;

  // Save new order values
  await updateCalendarInput(itemToMove);
}

async function updateCalendarInput(input: ContentCalendarInput) {
  isSavingNewOrderAndSourceValues.value = true;

  await ContentCalendarRepo.updateInput(props.customerId, input.id, input);
  if (getCalendarInputsResponse.value) {
    getCalendarInputsResponse.value.inputs = [...getCalendarInputsResponse.value.inputs.filter(i => i.id !== input.id), input];
  }

  isSavingNewOrderAndSourceValues.value = false;
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "./headers.scss";

// Initial step container =====================================================
.init-step-container {
  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__header-main {
    margin-bottom: 30px;
    padding-bottom: 0;
  }
}

// Header main title row ======================================================
.header-main {
  &__title-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 10px;
  }
}

// Initial step container (continued) =========================================
.init-step-container {
  &__calendar-inputs-list-wrap {
    margin-bottom: 45px;
  }

  &__calendar-inputs-list {
    padding: 0;
    margin: 0 0 20px;
    display: flex;
    flex-direction: column;
    list-style: none;
  }

  &__calendar-input-wrap {
    padding: 7px 0;
    position: relative;
    z-index: 0;
    list-style: none;

    &--drag-n-drop-ghost {
      &::before {
        content: "";
        width: 100%;
        height: calc(100% - 14px);
        border-radius: 6px;
        position: absolute;
        inset: 7px auto auto 0;
        z-index: 10;
        background: rgba(230, 230, 230, 1);
      }
    }

    &[data-cloned-element-for-photos-section-mouse-drag-event="true"]::before {
      display: none;
    }

    &:first-child {
      padding-top: 0;

      &::before {
        height: calc(100% - 7px);
        top: 0;
      }
    }

    &:last-child {
      padding-bottom: 0;

      &::before {
        height: calc(100% - 7px);
      }
    }

    &::after {
      width: 100%;
      height: 3px;
      border-radius: 100px;
      position: absolute;
      left: 0;
      background: #058587;
      pointer-events: none;
    }

    &--drop-indicator-on-top {
      &::after {
        content: "";
        top: -1px;
      }
    }
    &--drop-indicator-on-bottom {
      &::after {
        content: "";
        bottom: -2px;
      }
    }
  }

  &__calendar-input {
    transition: opacity 0.05s ease-in-out;
  }

  &__divider {
    margin-bottom: 35px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__header-secondary {
    margin-bottom: 25px;

    &--with-toggle {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
    }
  }

  &__whats-next-note {
    max-width: 60%;
    margin: 0 0 15px auto;
  }

  &__buttons-panel {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  &__interests-wrap {
    margin-bottom: 25px;
  }

  &__interests-textarea {
    width: 100%;
    height: 90px;
    padding: 12px;
    margin-bottom: 20px;
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
    resize: vertical;
    transition: border-color 0.2s ease-in-out;

    &::placeholder {
      color: #999;
    }

    &:focus {
      outline: none;
      border-color: #118689;
    }
  }

  &__interests-debug {
    margin-top: 40px;
  }

  &__social-accounts-wrap {
    margin-bottom: 45px;
  }

  &__schedule-wrap {
    margin-bottom: 45px;
  }

  &__calendar-name-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 5px;

    .header-secondary__title {
      margin-bottom: 0;
    }
  }

  &__calendar-name-wrap {
    margin-bottom: 22px;
  }
}

.channels {
  margin: 0 0 10px;
}
.channels--empty {
  margin: 0 0 10px;
  @include standardFont;
}
.channel {
  display: flex;
  align-items: center;
  margin: 6px 0;

  &:first-child {
    margin-top: 0;
  }
}
.channel__label {
  font-weight: 600;
  margin-right: 6px;
}
.channel__status-badge {
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(212, 51, 51, 0.1);
  color: #b42318;
  border: 1px solid rgba(180, 35, 24, 0.3);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}
.channel__id,
.channel__handle {
  color: rgba(0, 0, 0, 0.6);
  margin-left: 6px;
}
.channel__profile-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: rgba(0, 0, 0, 0.6);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
.channel-reauth-wrap {
  margin-top: 18px;
}
.schedule__legend {
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
}
.slot-item {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 6px 0;

  &__delete-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover:not(:disabled) {
      background-color: rgba(0, 0, 0, 0.05);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }
  }
}
.save-msg {
  font-weight: 600;
  font-size: 16px;
}
.debug-pre {
  background: #f7f7fb;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  padding: 10px;
  white-space: pre-wrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  margin-top: 10px;
}
.website-social-discover__toggle {
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  color: #667085;
  cursor: pointer;
  font: inherit;
  line-height: 1;
  letter-spacing: 0.08em;
}
.website-social-discover__toggle:hover {
  color: #101828;
}
.simple-select {
  padding: 6px 8px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background: white;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  // min-width: 60px;

  &:focus {
    outline: 2px solid #0ea5e9;
    outline-offset: 1px;
  }
}
.form-field__label {
  display: block;
  font-size: 14px;
  margin-bottom: 6px;
}

// Controls container (from SocialSettings.vue) ==================================
.controls-container {
  &__two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    align-items: start;
  }
}

// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .init-step-container {
    &__whats-next-note {
      max-width: 100%;
    }
  }

  .controls-container__two-col {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .init-step-container {
    &__whats-next-note {
      max-width: 100%;
    }
  }

  .controls-container__two-col {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>
